import React, { useCallback, useEffect, useState, } from 'react';
import PropTypes from 'prop-types';
import { money, } from '@payit/payit-formatter';
import AutopayService from '../../../service/AutopayService';

StatusReport.propTypes = {
  serviceOfferingId: PropTypes.string.isRequired,
  dueDate: PropTypes.string.isRequired,
};

export default function StatusReport({ serviceOfferingId, dueDate, }) {
  const [ aggregateBillData, setAggregateBillData, ] = useState(null);
  const [ bills, setBills, ] = useState(null);
  const [ statusLoadingError, setStatusLoadingError, ] = useState(null);
  const [ billsLoading, setBillsLoading, ] = useState(true);
  const [ billsLoadingError, setBillsLoadingError, ] = useState(null);
  const [ page, setPage, ] = useState(1);
  const [ pageSize, setPageSize, ] = useState(100);
  const [ gotoPage, setGotoPage, ] = useState('');

  const fetchStatusData = useCallback(() => {
    return AutopayService.getAggregateBillData(serviceOfferingId, dueDate).then(aggregateBillResponse => {
      setAggregateBillData(aggregateBillResponse);
      setStatusLoadingError(null);
    }).catch((err) => {
      setAggregateBillData(null);
      setStatusLoadingError(err.message);
    });
  }, [serviceOfferingId, dueDate]);

  const fetchBillsData = useCallback(() => {
    setBillsLoading(true);
    return AutopayService.getBillSummaries(serviceOfferingId, dueDate, dueDate, page - 1, pageSize).then(billSummaryData => {
      setBills(billSummaryData.bills);
      setBillsLoadingError(null);
      setBillsLoading(false);
    }).catch((err) => {
      setBills(null);
      setBillsLoadingError(err.message);
      setBillsLoading(false);
    });
  }, [serviceOfferingId, dueDate, page, pageSize]);

  useEffect(() => {
    fetchStatusData();
  }, [fetchStatusData]);

  useEffect(() => {
    fetchBillsData();
  }, [fetchBillsData]);

  const errors = [
    ...statusLoadingError ? [ `Error loading status data: ${statusLoadingError}`, ] : [ ],
    ...billsLoadingError ? [ `Error loading bills data: ${billsLoadingError}`, ] : [ ],
  ];
  if (errors.length > 0) {
    return (
      <div>
        {errors.map((e, index) => (<p key={`error-${index}`}>{e}</p>))}
      </div>
    );
  }
  if (!aggregateBillData || !bills) return (<p>Loading...</p>);
  const statusTotals = {};
  Object.keys(aggregateBillData.failureReasonCounts).forEach(status => {
    statusTotals[status] = Object.values(aggregateBillData.failureReasonCounts[status]).reduce((a, b) => a + b, 0);
  });
  const grandTotal = Object.values(statusTotals).reduce((a, b) => a + b, 0);
  if (grandTotal === 0) return (<p>No results.</p>);

  const statusTable = () => (
    <table className="atp-status-report-statuses">
      <thead>
        <tr>
          <th>Status</th>
          <th>Failure Reason</th>
          <th>Count</th>
          <th>Total</th>
        </tr>
      </thead>
      {Object.keys(aggregateBillData.failureReasonCounts).map((status, index) => (
        <tbody key={`status-${index}`}>
          <tr>
            <td>{status}</td>
            <td />
            <td />
            <td />
          </tr>
          {Object.keys(aggregateBillData.failureReasonCounts[status]).map((failureReason, failIndex) => (
            <tr key={`status-${index}-failure-${failIndex}`}>
              <td />
              <td>{failureReason}</td>
              <td>{aggregateBillData.failureReasonCounts[status][failureReason]}</td>
              <td />
            </tr>
          ))}
          <tr>
            <td>Total</td>
            <td />
            <td />
            <td>{statusTotals[status]}</td>
          </tr>
        </tbody>
      ))}
      <tbody>
        <tr>
          <td>Grand Total</td>
          <td />
          <td />
          <td>{grandTotal}</td>
        </tr>
      </tbody>
    </table>
  );

  const pageLimit = Math.ceil(grandTotal / pageSize);
  const pagination = () => (
    <div className="atp-status-pagination">
      <div>
        <button type="button" onClick={() => setPage(1)}>«</button>
        <button type="button" onClick={() => setPage(Math.max(1, page - 1))}>‹</button>
        Page {page} of {pageLimit}
        <button type="button" onClick={() => setPage(Math.min(pageLimit, page + 1))}>›</button>
        <button type="button" onClick={() => setPage(pageLimit)}>»</button>
      </div>
      <div>
        <button
          type="button"
          disabled={!gotoPage}
          onClick={() => {
            setPage(Math.min(pageLimit, Math.max(1, gotoPage)));
            setGotoPage('');
          }}
        >
          Goto Page
        </button>
        <input className="atp-status-page" type="number" placeholder={page} value={gotoPage} onChange={(e) => setGotoPage(e.target.value)} />
      </div>
      <div className="atp-status-loading-page">{billsLoading ? 'Loading...' : ''}</div>
      <div>
        Page Size
        <select
          name="pageSize"
          value={pageSize}
          onChange={(e) => {
            setPageSize(e.target.value);
            setPage(1);
          }}
        >
          <option>10</option>
          <option>25</option>
          <option>50</option>
          <option>100</option>
          <option>250</option>
          <option>500</option>
        </select>
      </div>
    </div>
  );

  const billHeaderRemapping = {
    id: 'Autopay Bill ID',
    serviceOfferingId: 'Service Offering ID',
    dueDate: 'Due Date',
    status: 'Status',
    failureReason: 'Failure Reason',
    transactionId: 'Transaction ID (with associated payment)',
    payableId: 'Vertical Bill ID',
    payableBalance: 'Payable Balance',
    preAuthorizedAmount: 'Pre-Auth Amount',
  };
  const billHeaders = () => Object.keys(bills[0])
    .map(field => billHeaderRemapping[field] || field)
    .map((field, index) => (<th key={`bill-header-${index}`}>{field}</th>));
  const billData = (bill) => Object.entries(bill).map(([ key, value, ], index) => (<td key={`bill-data-${index}`}>{formatBillData(key, value)}</td>));
  const formatBillData = (key, value) => {
    if (!value) return '';
    if ([ 'payableBalance', 'preAuthorizedAmount', ].includes(key)) {
      return money.printMoney(value);
    }
    return value.toString();
  };
  const billsTable = () => (
    <table className="atp-status-report-bills">
      <thead><tr>{billHeaders()}</tr></thead>
      <tbody>
        {bills.map((bill, index) => (
          <tr key={`bill-${index}`}>{billData(bill)}</tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="atp-status-report">
      <h5>Aggregate data</h5>
      {aggregateBillData.total ? (<div> Total Due: {money.printMoney(aggregateBillData?.total)}</div>) : '' }
      {statusTable()}
      <h5>Autopay Bills</h5>
      {pagination()}
      {billsTable()}
    </div>
  );
}
